import {
  WorkloadOptimizationMetricSpec,
  WorkloadOptimizationMetricTarget,
} from '@cast/types';

const targetTypeMap: Record<WorkloadOptimizationMetricTarget['type'], string> =
  {
    AVERAGE_VALUE: 'Average Value',
    VALUE: 'Value',
    UTILIZATION: 'Utilization',
    METRIC_TARGET_TYPE_UNSPECIFIED: 'Unspecified',
  };

type MetricData = {
  label: string;
  name: string;
  title: string;
  description?: string;

  inputLabel: string;
  value: string;
  isPercentage?: boolean;
};

export type Metrics = {
  resources: MetricData[];
  containerResources: MetricData[];
  objects: MetricData[];
  pods: MetricData[];
  externals: MetricData[];
};

export const getMetricsData = ({
  hpaMetrics,
}: {
  hpaMetrics: WorkloadOptimizationMetricSpec[];
}): Metrics => {
  return hpaMetrics.reduce<Metrics>(
    (acc, cur): Metrics => {
      if (cur.type === 'RESOURCE' && cur.resource) {
        const {
          name,
          target: { type, value },
        } = cur.resource;
        return {
          ...acc,
          resources: [
            ...acc.resources,
            {
              label: cur.type,
              title: 'Resource',
              name: name === 'cpu' ? 'CPU target' : 'Memory target',

              inputLabel: targetTypeMap[type],
              value,
              isPercentage: type === 'UTILIZATION',
            },
          ],
        };
      }

      if (cur.type === 'EXTERNAL' && cur.external) {
        const {
          metric: { name },
          target: { type, value },
        } = cur.external;
        return {
          ...acc,
          externals: [
            ...acc.externals,
            {
              label: cur.type,
              name,
              title: 'External',

              inputLabel: targetTypeMap[type],
              value,
              isPercentage: type === 'UTILIZATION',
            },
          ],
        };
      }

      if (cur.type === 'OBJECT' && cur.object) {
        const {
          metric: { name },
          target: { type, value },
        } = cur.object;
        return {
          ...acc,
          objects: [
            ...acc.objects,
            {
              label: cur.type,
              name,
              title: 'Object',

              inputLabel: targetTypeMap[type],
              value,
              isPercentage: type === 'UTILIZATION',
            },
          ],
        };
      }

      if (cur.type === 'PODS' && cur.pods) {
        const {
          metric: { name },
          target: { type, value },
        } = cur.pods;
        return {
          ...acc,
          pods: [
            ...acc.pods,
            {
              label: cur.type,
              name,
              title: 'Pods',

              inputLabel: targetTypeMap[type],
              value,
              isPercentage: type === 'UTILIZATION',
            },
          ],
        };
      }

      if (cur.type === 'CONTAINER_RESOURCE' && cur.containerResource) {
        const {
          name,
          container,
          target: { type, value },
        } = cur.containerResource;
        return {
          ...acc,
          containerResources: [
            ...acc.containerResources,
            {
              label: 'CONTAINER RESOURCE',
              name,
              description: container,
              title: 'Container Resource',

              inputLabel: targetTypeMap[type],
              value,
              isPercentage: type === 'UTILIZATION',
            },
          ],
        };
      }

      return acc;
    },
    {
      // this initial object all defined the order the metrics are shown
      resources: [],
      containerResources: [],
      objects: [],
      pods: [],
      externals: [],
    }
  );
};
